/**
 * E2E auth bypass shim.
 *
 * Playwright cannot drive real Cognito sign-up/sign-in from a CI box,
 * so we wire a build-time flag that swaps Amplify's auth surface for a
 * deterministic in-memory fake. The flag is read from
 * `import.meta.env.VITE_E2E_AUTH_BYPASS` and is only honoured in the
 * `local` build so it can never leak into staging or production
 * bundles even if the env var is set accidentally.
 *
 * We read `VITE_APP_ENV` directly here (instead of importing
 * `lib/env`) because `lib/env` throws at module load when Cognito vars
 * are missing — Vitest unit tests don't supply them, but they should
 * still be able to import the auth surface that depends on this file.
 *
 * Consumers:
 *   - `features/auth/api/amplify-auth.ts` checks `isE2EAuthBypass` and
 *     dispatches to `fakeAuth.*` instead of the real SDK calls.
 *   - Playwright's webServer command sets `VITE_E2E_AUTH_BYPASS=1`.
 *
 * `FakeAuthUser` is intentionally declared here (instead of importing
 * `AuthUser` from `features/auth/types`) because the import-boundary
 * lint forbids `lib/` from reaching into a feature. The shape is kept
 * structurally identical to `AuthUser`; if you change one, change the
 * other.
 */

export interface FakeAuthUser {
  userId: string;
  email: string;
  emailVerified: boolean;
  displayName: string | null;
}

function envValue(key: string): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return env[key] ?? '';
}

const isLocalBuild =
  envValue('VITE_APP_ENV') !== 'production' && envValue('VITE_APP_ENV') !== 'staging';
export const isE2EAuthBypass: boolean = isLocalBuild && envValue('VITE_E2E_AUTH_BYPASS') === '1';

const FAKE_USER: FakeAuthUser = {
  userId: 'e2e-test-user',
  email: 'e2e@avatarfactory.test',
  emailVerified: true,
  displayName: 'E2E Tester',
};

interface FakeAuthState {
  signedIn: boolean;
  user: FakeAuthUser;
}

// Persist the fake session in localStorage so Playwright's storageState
// can capture it. This lets `support/global-setup.ts` produce ONE
// authenticated storageState — for both the real-Cognito path and this
// bypass fallback — so every spec is authenticated uniformly without a
// per-test login fork. (Real Amplify also persists its tokens in
// localStorage, so the two paths are symmetric.)
const STORAGE_KEY = '__e2e_bypass_auth__';

function loadState(): FakeAuthState {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { signedIn: false, user: FAKE_USER };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { signedIn: false, user: FAKE_USER };
    const parsed = JSON.parse(raw) as Partial<FakeAuthState>;
    return {
      signedIn: parsed.signedIn === true,
      user: { ...FAKE_USER, ...(parsed.user ?? {}) },
    };
  } catch {
    return { signedIn: false, user: FAKE_USER };
  }
}

function persistState(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Best-effort; a storage-quota error must not break the fake auth.
  }
}

const state: FakeAuthState = loadState();

type AuthEvent = 'signedIn' | 'signedOut';
type Listener = (event: AuthEvent) => void;

const listeners = new Set<Listener>();

function emit(event: AuthEvent): void {
  for (const listener of listeners) listener(event);
}

/** Optional identity overrides supplied at sign-in / sign-up time. */
export interface FakeSignInOptions {
  email?: string;
  displayName?: string | null;
}

export const fakeAuth = {
  getUser(): FakeAuthUser | null {
    return state.signedIn ? state.user : null;
  },
  /**
   * Sign the fake user in. Optional overrides let a caller mirror real
   * Cognito — e.g. `signUp` passes the typed email + full name so
   * identity-personalised UI (the dashboard greeting) is faithful.
   */
  signIn(options?: FakeSignInOptions): void {
    if (options) {
      state.user = {
        ...state.user,
        ...(options.email ? { email: options.email } : {}),
        ...(options.displayName !== undefined ? { displayName: options.displayName } : {}),
      };
    }
    state.signedIn = true;
    persistState();
    emit('signedIn');
  },
  signOut(): void {
    state.signedIn = false;
    state.user = FAKE_USER;
    persistState();
    emit('signedOut');
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
